import React, { useEffect, useState } from 'react';
import { Row, Col, Spin, Button, Modal, Form, Card, Typography, InputNumber, theme } from 'antd';
import { message } from '../../services/toastMessage';
import { PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { IconTruck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../components/Layouts/Layout';
import EmptyState from '../../components/EmptyState';
import ConfirmBottomSheet from '../../components/ConfirmBottomSheet';
import FloatingLabelInput from '../../components/FloatingLabelInput';
import EmojiTextArea from '../../components/EmojiTextArea';
import {
  getMyTransportVehicles,
  createTransportVehicle,
  updateTransportVehicle,
  deleteTransportVehicle,
} from '../../services/TransportVehicleService';
import { ITransportVehicle } from '../../interfaces/heavyDutyMachinery/ITransportVehicle';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { useAppBreakpoint } from '../../hooks/useAppBreakpoint';
import { pillToneStyle } from '../../theme/creamyTokens';

const { Text, Title } = Typography;

interface TransportVehicleFormValues {
  name: string;
  licensePlate: string;
  brand?: string;
  description?: string;
  fuelConsumptionPer100Km?: number;
}

/**
 * A host's own transport fleet -- same "catalog at the host level" pattern
 * as MyMachineryAccessories/MyOperators, added to HOST_TOOLS_NAV's own
 * host-only nav as a peer to those. Replaces the create/edit wizard's
 * earlier hardcoded 3-option delivery-vehicle placeholder (see
 * heavyDutyMachinerySpaceOptions.ts's own history) -- a vehicle registered
 * here becomes selectable as a specific Machinery's own DeliveryVehicleId in
 * that machine's own delivery section, not here (same "linking happens on
 * the machine's own form" split as MachineryAccessoryLinkSection).
 */
const MyTransportVehiclesContent: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [vehicles, setVehicles] = useState<ITransportVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<ITransportVehicle | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [deletingVehicle, setDeletingVehicle] = useState<ITransportVehicle | undefined>(undefined);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [form] = Form.useForm<TransportVehicleFormValues>();

  const load = () => {
    setLoading(true);
    getMyTransportVehicles()
      .then(setVehicles)
      .catch(() => message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_LOAD_ERROR)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreateModal = () => {
    setEditingVehicle(undefined);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (vehicle: ITransportVehicle) => {
    setEditingVehicle(vehicle);
    form.setFieldsValue({
      name: vehicle.name,
      licensePlate: vehicle.licensePlate,
      brand: vehicle.brand,
      description: vehicle.description,
      fuelConsumptionPer100Km: vehicle.fuelConsumptionPer100Km,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload = {
      name: values.name.trim(),
      licensePlate: values.licensePlate.trim(),
      brand: values.brand?.trim() || undefined,
      description: values.description?.trim() || undefined,
      fuelConsumptionPer100Km: values.fuelConsumptionPer100Km,
    };

    setSaving(true);
    try {
      const saved = editingVehicle
        ? await updateTransportVehicle(editingVehicle.id, payload)
        : await createTransportVehicle(payload);
      setVehicles((prev) => (editingVehicle ? prev.map((v) => (v.id === saved.id ? saved : v)) : [saved, ...prev]));
      message.success(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_SAVE_SUCCESS));
      setModalOpen(false);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (vehicle: ITransportVehicle) => {
    try {
      const updated = await updateTransportVehicle(vehicle.id, { isActive: !vehicle.isActive });
      setVehicles((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_STATUS_UPDATE_ERROR));
    }
  };

  const handleDeleteVehicle = async () => {
    if (!deletingVehicle) return;
    setDeleteBusy(true);
    try {
      await deleteTransportVehicle(deletingVehicle.id);
      setVehicles((prev) => prev.filter((v) => v.id !== deletingVehicle.id));
      setDeletingVehicle(undefined);
    } catch (error: any) {
      message.error(error.message || translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_STATUS_UPDATE_ERROR));
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <AppLayout>
      <div style={{ width: '100%', maxWidth: 1100 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_NAV_LABEL)}
            </Title>
            <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_PAGE_SUBTITLE)}</Text>
          </div>
          {!loading && vehicles.length > 0 && (
            <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={openCreateModal}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_CREATE_BUTTON)}
            </Button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : vehicles.length === 0 ? (
          <EmptyState
            icon={<IconTruck size={24} />}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_EMPTY_TITLE)}
            description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_EMPTY_DESCRIPTION)}
            action={
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLES_CREATE_BUTTON)}
              </Button>
            }
          />
        ) : (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            {vehicles.map((vehicle) => (
              <Col xs={24} sm={12} md={8} key={vehicle.id}>
                <Card bordered style={{ borderRadius: 18, opacity: vehicle.isActive ? 1 : 0.6 }} styles={{ body: { padding: 16 } }}>
                  <span
                    style={{
                      display: 'inline-block',
                      alignSelf: 'flex-start',
                      marginBottom: 4,
                      padding: '2px 10px',
                      borderRadius: 999,
                      fontSize: 12,
                      fontWeight: 600,
                      ...pillToneStyle(vehicle.isActive ? 'success' : 'default', token),
                    }}
                  >
                    {translate(t, vehicle.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_ACTIVE_STATUS : TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_INACTIVE_STATUS)}
                  </span>

                  <Title level={5} style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {vehicle.name}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                    {vehicle.licensePlate}{vehicle.brand ? ` · ${vehicle.brand}` : ''}
                  </Text>
                  {vehicle.description && (
                    <Text type="secondary" style={{ fontSize: 13, display: 'block', marginTop: 4 }}>
                      {vehicle.description}
                    </Text>
                  )}
                  {vehicle.fuelConsumptionPer100Km != null && (
                    <Text strong style={{ display: 'block', marginTop: 10, fontSize: 15 }}>
                      {vehicle.fuelConsumptionPer100Km.toFixed(1)} L/100km
                    </Text>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle('default', token) }}
                        icon={<EditOutlined />}
                        onClick={() => openEditModal(vehicle)}
                      >
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_EDIT_BUTTON)}
                      </Button>
                      <Button
                        shape="round"
                        block
                        style={{ height: 40, border: 'none', ...pillToneStyle(vehicle.isActive ? 'error' : 'success', token) }}
                        icon={vehicle.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                        onClick={() => toggleActive(vehicle)}
                      >
                        {translate(t, vehicle.isActive ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_DEACTIVATE_BUTTON : TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_REACTIVATE_BUTTON)}
                      </Button>
                    </div>
                    <Button
                      shape="round"
                      block
                      style={{ height: 40, border: 'none', ...pillToneStyle('error', token) }}
                      icon={<DeleteOutlined />}
                      onClick={() => setDeletingVehicle(vehicle)}
                    >
                      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CARD_DELETE_BUTTON)}
                    </Button>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        <Modal
          open={modalOpen}
          title={translate(t, editingVehicle ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_MODAL_EDIT_TITLE : TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_MODAL_CREATE_TITLE)}
          onCancel={() => setModalOpen(false)}
          onOk={handleSave}
          confirmLoading={saving}
          okText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_SAVE_BUTTON)}
          okButtonProps={{ shape: 'round' }}
          cancelButtonProps={{ shape: 'round' }}
          destroyOnClose
        >
          <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
            <Form.Item name="name" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_NAME_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="licensePlate" rules={[{ required: true, message: translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_FIELD_REQUIRED) }]}>
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_LICENSE_PLATE_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="brand">
              <FloatingLabelInput label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_BRAND_LABEL)} disabled={saving} />
            </Form.Item>
            <Form.Item name="description">
              <EmojiTextArea placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_DESCRIPTION_LABEL)} rows={2} disabled={saving} maxLength={500} />
            </Form.Item>
            <Form.Item label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_TRANSPORT_VEHICLE_FUEL_CONSUMPTION_LABEL)} name="fuelConsumptionPer100Km">
              <InputNumber min={0} step={0.1} addonAfter="L/100km" inputMode="decimal" style={{ width: '100%' }} disabled={saving} />
            </Form.Item>
          </Form>
        </Modal>
      </div>

      <ConfirmBottomSheet
        open={!!deletingVehicle}
        title={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_TITLE)}
        description={deletingVehicle?.name}
        cancelText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_KEEP)}
        confirmText={translate(t, TRANSLATIONS.DELETE_ITEM_CONFIRM_DELETE)}
        confirmLoading={deleteBusy}
        onConfirm={handleDeleteVehicle}
        onClose={() => setDeletingVehicle(undefined)}
      />
    </AppLayout>
  );
};

// Split into a wrapper + Content component so that a scoped theme can
// be added here without moving anything: theme.useToken() only sees a
// nested ConfigProvider's tokens from a component mounted UNDER it, not
// from the one rendering the provider. There is nothing scoped here
// today -- coral is the app-wide primary (see theme/appTheme).
const MyTransportVehicles: React.FC = () => {
  const { isMobile } = useAppBreakpoint();
  return isMobile ? (
    <MyTransportVehiclesContent />
  ) : (
    <MyTransportVehiclesContent />
  );
};

export default MyTransportVehicles;
